import {Injectable, EventEmitter, ApplicationRef/*, NgZone*/} from '@angular/core';
import {Observable} from 'rxjs/Observable';

// Services
import {ProductCategoriesService} from './product-categories-service';
import {ProductsService} from './products-service';
import {CartService} from './cart-service';
import {SceneService} from './scene-service'; // TODO: Decouple
import {GeneratorsService} from './generators-service'; // TODO: Decouple

// Classes
import {ProductCategory} from '../classes/product-category';
import {Product} from '../classes/product';

// Interfaces
import {Taxonomy} from '../classes/product';

let debug, debugOff = function (...any) { };
process.env.NODE_ENV !== 'production' && (debug = require('debug')('app:CatalogService'));

/**
 * Catalog service is concerned with handling and normalizing products and product categories data.
 * It also exposes a few convenience methods that allow direct access to `_products` and `_categories` data from `ProductsService` and `ProductCategoriesService`.
 * Can be improved to handle streaming
 * <!><!><!> This service contains a lot of hardcoded business logic. If specs were more clearly defined from the beginning a better approach could have been used.
 */
@Injectable()
export class CatalogService {

    private _globals = {
        selectedGenerator: null, // TODO: Decouple
        selectedTowelWarmer: null, // TODO: Decouple
        selectedControl: null // TODO: Decouple
    };

    private _appService; // TODO: Decouple

    // Should be retrieved from the server
    // TODO: Decouple, in Vspa maybe
    public imgPaths = {
        categories: 'media/categories/',
        products: 'media/products/',
        options: 'media/options/',
        scene: 'media/scene/',
    };


    public prevSelectedCategory;

    public events = {
        catalogDataLoadedAsync: new EventEmitter(),
        categorySelected: new EventEmitter() // TODO: Should productCategoriesNavLsit use the same event?
    };
    private _state = {
        catalogDataIsLoaded: false
    };


    constructor(
        private _productCategoriesService: ProductCategoriesService,
        private _productsService: ProductsService,
        private _cartService: CartService,
        private _sceneService: SceneService, // TODO: Decouple
        private _generatorsService: GeneratorsService, // TODO: Decouple
        private _applicationRef: ApplicationRef,
        // private _ngZone: NgZone
    ) {
        //'Construct catalog service');

    }


    public init(AppService) {
        //'Initialise catalog service');

        // TODO: Find a better method
        this._appService = AppService;
        this._productsService.init(this, AppService);
        this._productCategoriesService.init(this);
        this._cartService.init(this, this.applyVspaProdAddLogic, this.applyVspaProdRemoveLogic);
        this._sceneService.init(this);
        this._productsService.events.productStateRefreshed.subscribe(event => {
            this.applyVspaProdStateRefreshLogic(event.product);
        })

    }


    public getCatalogDataAsync() {
        //'Get all catalog data');

        let self = this;

        // Join multiple observables and in order to detect final response
        let observable = Observable.forkJoin(
            this._productCategoriesService.getAllCategoriesAsync(),
            this._productsService.getAllProductsAsync()
        );

        // Handle catalog data
        observable.subscribe(
            response => self._handleCatalogData(response),
            error => console.error(error)
        );

        return observable;

    }


    public getGlobals() {
        //'Get catalog globals');
        return this._globals;
    }


    public checkCatalogDataIsLoaded() {
        debugOff('Check catalog data is loaded:', this._state.catalogDataIsLoaded);
        return this._state.catalogDataIsLoaded;
    }


    public checkCurrentCategoryChanged(changes) {
        debugOff('Check if current products category has changed');

        let currentCategoryId = changes.catalogState.currentValue.currentCategoryId,
            previousCategoryId = changes.catalogState.previousValue.currentCategoryId;

        if (changes.catalogState !== undefined &&
            previousCategoryId !== currentCategoryId
        ) {
            debugOff('Current products category changed');
            debugOff('Previous category id:', previousCategoryId);
            debugOff('Current category id:', currentCategoryId);
            return currentCategoryId;
        }
        return false;
    }


    public checkRenderModeChanged(changes) {
        debugOff('Check if catalog render mode has changed');

        let currentRenderMode = changes.catalogState.currentValue.renderMode,
            previousRenderMode = changes.catalogState.previousValue.renderMode;

        if (changes.catalogState !== undefined &&
            previousRenderMode !== currentRenderMode
        ) {
            debugOff('Current products category changed');
            debugOff('Previous category id:', previousRenderMode);
            debugOff('Current category id:', currentRenderMode);
            return currentRenderMode;
        }
        return false;
    }


    public getProductCategoriesList = this._productCategoriesService.getProductCategoriesList
        .bind(this._productCategoriesService);


    public getProductCategoryById = this._productCategoriesService.getProductCategoryById
        .bind(this._productCategoriesService);


    public getRootCategory = this._productCategoriesService.getRootCategory
        .bind(this._productCategoriesService);


    public getFeaturedProductThumb = this._productCategoriesService.getFeaturedProductThumb
        .bind(this._productCategoriesService);


    public getCategoryThumbnailPath = this._productCategoriesService.getCategoryThumbnailPath
        .bind(this._productCategoriesService);


    public shallowCloneSubcategories = this._productCategoriesService.shallowCloneSubcategories
        .bind(this._productCategoriesService);


    public shallowCloneCategory = this._productCategoriesService.shallowCloneCategory
        .bind(this._productCategoriesService);


    public getAllProducts = this._productCategoriesService.getAllProducts
        .bind(this._productCategoriesService);


    public getProductsList = this._productsService.getProductsList
        .bind(this._productsService);


    public getProductsByCategoryId = this._productsService.getProductsByCategoryId
        .bind(this._productsService);


    public getProductById = this._productsService.getProductById
        .bind(this._productsService);


    public getProductOptionImage = this._productsService.getProductOptionImage
        .bind(this._productsService);


    public getProductOptionName = this._productsService.getProductOptionName
        .bind(this._productsService);

    /** Can be improved to handle streaming data.
     */
    private _handleCatalogData(response) {
        //'Handle catalog data');
        debugOff('Catalog data:', response);

        // Normalise data
        this._normaliseCatalogData();

        // Update state, emit event
        this._state.catalogDataIsLoaded = true;
        this.events.catalogDataLoadedAsync.emit({
            productCategoriesList: this._productCategoriesService.getProductCategoriesList(),
            rootCategory: this._productCategoriesService.getRootCategory()
        });
    }

    /** Can be improved to handle streaming data.
     *  @since 2.1.0 */
    private _normaliseCatalogData() {
        //'Normalise catalog data');
        let products = this.getProductsList();
        this._productCategoriesService.generateCategoriesThree();
        this._productCategoriesService.updateCategoriesSortOrder();
        this._productCategoriesService.appendProductAPI(products);
        this._productsService.initProducts(products, this._globals);
        this._productsService.appendProductAPI(products);
        this._cartService.appendProductAPI(products);
        this._sceneService.appendProductAPI(products);
        this._sortProductsInCategories();
        this._updateProductsSortOrder();

        // // TODO: Find better way, use lifecycle hooks
        // // Allow towel warmers accessories to render but then set it up to hidden
        // setTimeout( () => {
        //     let towelWarmersAccessories = this.getProductCategoryById( 'towel-warmers-accessories' );
        //     towelWarmersAccessories.isVisible = false;
        // }, 1000);
    }

    /** Can be improved to handle streaming data via categories param.
     */
    private _sortProductsInCategories() {
        //'Sort all products in categories');
        let self = this;

        // Get product categories list
        let prodCategsList = this._productCategoriesService.getProductCategoriesList();
        debugOff('Product categories:', prodCategsList);

        // Add products to parent category
        prodCategsList.forEach(category => {
            category.products = self.getProductsByCategoryId(category.id);
            debugOff('"' + category.id + '" products:', category.products);
        });

        //'Sorted products in categories:', prodCategsList);
    }

    /** Can be improved to handle streaming data via categories param.
     */
    private _updateProductsSortOrder() {
        //'Update products sort order for all categories');

        // Get product categories list
        let prodCategsList = this._productCategoriesService.getProductCategoriesList();
        debugOff('Product categories:', prodCategsList);

        prodCategsList.forEach((category: ProductCategory) => {

            // Prevent traversing empty arrays
            if (category.products === undefined ||
                category.products.length === 0
            ) {
                return;
            }

            //'Sort products of "' + category.id + '"');

            // Set sort default value if missing
            category.products.forEach((product: Product) => {
                if (product.taxonomy === undefined) {
                    product.taxonomy = <Taxonomy>{};
                }
                if (product.taxonomy.sortOrder === undefined) {
                    product.taxonomy.sortOrder = {};
                    product.taxonomy.sortOrder[category.id] = 0;
                }
            });

            // Sort subcategories
            category.products.sort((a, b) => {
                return a.taxonomy.sortOrder[category.id] - b.taxonomy.sortOrder[category.id];
            });
            //'Sorted products of "' + category.id + '":', category.products);
        });

    }

    // TODO: All three apply product logic methods need refactoring.
    public applyVspaProdStateRefreshLogic(product: Product) {
        // console.log('Apply Vspa Prod State Refresh Logic - Product', product);

        if (product.taxonomy &&
            product.taxonomy.categories[0] === 'towel-warmers-200' &&
            product.isAddedToCart === true
        ) {
            // console.log('product', product);

        }

    }

    // TODO: Decouple. Refactor. Move to AppCatalogService
    // Can these rules be achieved programmatically / from data?
    // <!> Some comments are out of sync

    public applyVspaProdAddLogic(product: Product) {
        //'Apply Vspa logic');
        

        //// <!> This rule seems outdated, product code is not up to date
        //
        //
        //if (product.code.toUpperCase() === 'XDREAM') {
        //    //'Vspa Logic - Added "XDREAM" product to cart');
        //
        //    // Hide Spa therapies category if xDream is selected
        //    let spaTherapies = this.getProductCategoryById('spa-therapies');
        //    spaTherapies.isVisible = false;
        //    //'Vspa Logic - Hide spa therapies category');
        // }

        if (product.taxonomy.currentCategoryId === 'isteamx-touch-screen-controls' ||
            product.taxonomy.currentCategoryId === 'airtempo-wireless-controls' ||
            product.taxonomy.currentCategoryId === 'itempo-controls'||
            product.taxonomy.currentCategoryId === 'itempo-plus-controls'
        ) {
            //'Vspa Logic - Remove package / individual control from iSteam, AirTempo, iTempo if another is selected');

            // Remove package / individual control from iSteam, AirTempo, iTempo if another is selected
            // TODO: Refactor Cart. Remove from multiple categories
            // TODO: Refactor Cart. Remove product from children subcategories
            let iSteamTouchScreenControls = this.getProductCategoryById('isteamx-touch-screen-controls');
            this._cartService.removeProductsByCategory(iSteamTouchScreenControls);
            let airTempoWirelessControls = this.getProductCategoryById('airtempo-wireless-controls');
            this._cartService.removeProductsByCategory(airTempoWirelessControls);
            let itempocontrols = this.getProductCategoryById('itempo-controls');
            this._cartService.removeProductsByCategory(itempocontrols);
            let itempopluscontrols = this.getProductCategoryById('itempo-plus-controls');
            this._cartService.removeProductsByCategory(itempopluscontrols);
        }



        if (product.taxonomy.currentCategoryId === 'isteamx-touch-screen-controls' ||
            product.taxonomy.currentCategoryId === 'airtempo-wireless-controls' ||
            product.taxonomy.currentCategoryId === 'itempo-controls'||
            product.taxonomy.currentCategoryId === 'itempo-plus-controls'
        ) {

            //'Product code:', product.code.toUpperCase());

            // Allow linear steamhead for individual controls


            if (product.code.toUpperCase() !== 'ISTEAMX_PACKAGE' &&
                product.code.toUpperCase() !== 'AIRTEMPO_PACKAGE' &&
                product.code.toUpperCase() !== 'ITEMPO_PACKAGE' &&
                product.code.toUpperCase() !== 'ITEMPO_PLUS_PACKAGE'
            ) {

                //'Vspa Logic - Hide linear steamhead from categories if selected control has linear option selected');

                // TODO: Use selectedControl from dedicated service.
                let linearSteamHead = this.getProductById('LINEAR_STEAMHEAD');
                linearSteamHead.isVisible = false;
                // this._cartService.removeProduct( linearSteamHead ); // Could be implemented

            }

            if (product.code.toUpperCase() !== 'ISTEAMX_PACKAGE' &&
            product.code.toUpperCase() !== 'AIRTEMPO_PACKAGE' &&
            product.code.toUpperCase() !== 'ITEMPO_PACKAGE' &&
            product.code.toUpperCase() !== 'ITEMPO_PLUS_PACKAGE'
        ) {

            //'Vspa Logic - Hide linear steamhead from categories if selected control has linear option selected');

            // TODO: Use selectedControl from dedicated service.
            let linearSteamHead = this.getProductById('LINEAR_STEAMHEAD');
            linearSteamHead.isVisible = false;
            // this._cartService.removeProduct( linearSteamHead ); // Could be implemented

        }

        }

        if (product.taxonomy.currentCategoryId === 'control-packages') {
            //'Vspa Logic - Remove products from "individual-controls" category');

            // Remove control package from cart if individual control is selected
            let individualControls = this.getProductCategoryById('individual-controls');
            this._cartService.removeProductsByCategory(individualControls);
        }

        if (product.taxonomy.currentCategoryId === 'individual-controls') {
            //'Vspa Logic - Remove products from "control-packages" category');

            // Remove individual control from cart if control package is selected
            let controlPackages = this.getProductCategoryById('control-packages');
            this._cartService.removeProductsByCategory(controlPackages);
        }

        if (product.code.toUpperCase() === 'ISTEAMX_PACKAGE' ||
            product.code.toUpperCase() === 'AIRTEMPO_PACKAGE' ||
            product.code.toUpperCase() === 'ITEMPO_PLUS_PACKAGE' ||
            product.code.toUpperCase() === 'ITEMPO_PACKAGE'
        ) {
            // // 'Vspa Logic - Remove "control-packages" product from cart' );

            // Display control accessories if control package is added to cart
            let controlsAccessories = this.getProductCategoryById('controls-accessories');
            controlsAccessories.isVisible = true;
            //'Vspa Logic - Display control accessories category');
        }

        if (
        product.code.toUpperCase() === 'BASIC_BUTLER_PACKAGE'
        ) {
            // // 'Vspa Logic - Remove "control-packages" product from cart' );

            // Display control accessories if control package is added to cart
            let controlsAccessories = this.getProductCategoryById('controls-accessories-2');
            controlsAccessories.isVisible = true;
            //'Vspa Logic - Display control accessories category');
        }

        //if (product.code.toUpperCase() === 'ISTEAMX_PACKAGE') {
          //  //'Vspa Logic - Added "iSteam" product to cart');

            // Hide spa therapies if XDREAM package is added to cart
         //   let spaTherapies = this.getProductCategoryById('spa-therapies');

            // This is already too much, changing prices depending on selected products
        //    spaTherapies.products[0].price = 1200;
        //    //'Changed AudioWizard price:', spaTherapies.products[0].price);
     //   }

     
 //    if (product.code.toUpperCase() === '_BLISS_PACKAGE') 
//     {
//     let twaccesory2 = this.getProductById('_SHOWERVALVE');
 //    twaccesory2.isVisible = false;         
 //} else{
 //    let twaccesory3 = this.getProductById('_SHOWERVALVE');
 //    twaccesory3.isVisible = true;
 //}
        
         // accessories exceptions for the when select ISTEAMX_PACKAGE
        if (product.code.toUpperCase() === 'ISTEAMX_PACKAGE') { 

            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = true;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = false;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = false;
            let twaccesory5 = this.getProductById('CHROMASTEAMDUOX');
            twaccesory5.isVisible = true;
            //let twaccesory6 = this.getProductById('SHOWERVALVE');
            //twaccesory6.isVisible = true;
            
        } else
        {
            let twaccesory5 = this.getProductById('CHROMASTEAMDUOX');
            twaccesory5.isVisible = false;
            //let twaccesory6 = this.getProductById('SHOWERVALVE');
            //twaccesory6.isVisible = false;
        }

        if (product.code.toUpperCase() === 'XBUTLER_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = true;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = false;
            //let twaccesory6 = this.getProductById('SPEAKERS');
            //twaccesory6.isVisible = true;
          
        }
        else if (product.code.toUpperCase() === 'XDREAM_PACKAGE')
        {
             // hide stuff
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = false;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
            //let twaccesory6 = this.getProductById('SPEAKERS');
            //twaccesory6.isVisible = false;
            let twaccesory8 = this.getProductById('AROMASTEAM');
            twaccesory8.isVisible = false;
            let twaccesory9 = this.getProductById('AROMA_STEAM_OIL_1L');
            twaccesory9.isVisible = true;
        }


        if (product.code.toUpperCase() === 'XDREAM_PACKAGE' ||
            product.code.toUpperCase() === 'BLISS_PACKAGE') 
            {
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = false; 
            let twaccesory8 = this.getProductById('AROMASTEAM');
            twaccesory8.isVisible = false;           
        } else{
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = true;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true; 
            let twaccesory8 = this.getProductById('AROMASTEAM');
            twaccesory8.isVisible = true; 
        }


        //exceptions for the when select AIRTEMPO_PACKAGE

        if (product.code.toUpperCase() === 'AIRTEMPO_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
        }

         if (product.code.toUpperCase() === 'AIRBUTLER_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
            let twaccesory7 = this.getProductById('AROMASTEAM');
            twaccesory7.isVisible = true;
            let twaccesory8 = this.getProductById('AROMA_STEAM_OIL_1L');
            twaccesory8.isVisible = true;
        }

            if (product.code.toUpperCase() === 'BUTLER_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
    
        }

            if (product.code.toUpperCase() === 'ITEMPO_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
        }

            if (product.code.toUpperCase() === 'ITEMPO_PLUS_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
        }

        if (product.code.toUpperCase() === 'BASIC_BUTLER_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = false;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = false;
            //let twaccesory6 = this.getProductById('SPEAKERS');
            //twaccesory6.isVisible = true;
            let twaccesory8 = this.getProductById('AROMASTEAM');
            twaccesory8.isVisible = false;
            let twaccesory9 = this.getProductById('AROMA_STEAM_OIL_1L');
            twaccesory9.isVisible = false;
            
        }
    


        if (product.code.toUpperCase() === 'XDREAM_PACKAGE' ||
            product.code.toUpperCase() === 'XBUTLER_PACKAGE' ||
            product.code.toUpperCase() === 'AIRBUTLER_PACKAGE' ||
            product.code.toUpperCase() === 'BUTLER_PACKAGE'
        ) {
            // // 'Vspa Logic - Added "control-packages" product to cart' );

            // Hide control accessories category if control package is selected
            let controlsAccessories = this.getProductCategoryById('controls-accessories');
            controlsAccessories.isVisible = false;
            //'Vspa Logic - Hide control accessories category');

            // Remove control accessories from cart if control package is selected
            this._cartService.removeProductsByCategory(controlsAccessories);
        }


        if (product.code.toUpperCase() === 'VALET_PACKAGE') {
            let towelWarmersAccessories = this.getProductCategoryById('towel-warmers-accessories');
            //'Vspa Logic - Remove towel accessorie if VALET is selected');
            this._cartService.removeProductsByCategory(towelWarmersAccessories);
        }

        // <!> Not the best approach, many drawbacks, finish options don;t stay in sync. A better solution is running this logic on product state refresh.
        // if (product.code.toUpperCase() === 'VALET_PACKAGE' ||
        //     product.code.toUpperCase() === 'ROBE_HOOK' ||
        //     product.code.toUpperCase() === 'DIGITAL_TIMER' 
        // ) {
        //     //'Vspa Logic - Sync towel warmer accessories to current towel warmer');

        //     let towelWarmers200 = this.getProductCategoryById('towel-warmers-200');
        //     let cartTowelWarmer: any = this._cartService.getCartProductsByCategory(towelWarmers200);
        //     console.log('cartTowelWarmer', cartTowelWarmer);
        //     let options: any = product.options;
        //     options.selected.finish = cartTowelWarmer[0].options.selected.finish
        // }

        if (product.taxonomy.currentCategoryId === 'towel-warmers-200') {// ||
            // product.taxonomy.currentCategoryId === 'towel-warmers-300' ||
            // product.taxonomy.currentCategoryId === 'towel-warmers-500') {
            //'Vspa Logic - Added "towel-warmers-200" category product to cart');

            // Display towel warmers accessories if Towel warmer S200 is selected
            let towelWarmerAccessories = this.getProductCategoryById('towel-warmers-accessories');
            towelWarmerAccessories.isVisible = true;
            //'Vspa Logic - Display towel warmers accessories category');
        }

        // Remove towel warmers from other series/categories
        if (product.taxonomy.currentCategoryId === 'towel-warmers-200' ||
            product.taxonomy.currentCategoryId === 'towel-warmers-300' ||
            product.taxonomy.currentCategoryId === 'towel-warmers-500' ||
            product.taxonomy.currentCategoryId === 'towel-warmers-800'
        ) {
            let towelWarmers200 = this.getProductCategoryById('towel-warmers-200');
            product.taxonomy.currentCategoryId !== 'towel-warmers-200' &&
                this._cartService.removeProductsByCategory(towelWarmers200);

            let towelWarmers300 = this.getProductCategoryById('towel-warmers-300');
            product.taxonomy.currentCategoryId !== 'towel-warmers-300' &&
                this._cartService.removeProductsByCategory(towelWarmers300);

            let towelWarmers500 = this.getProductCategoryById('towel-warmers-500');
            product.taxonomy.currentCategoryId !== 'towel-warmers-500' &&
                this._cartService.removeProductsByCategory(towelWarmers500);

            let towelWarmers800 = this.getProductCategoryById('towel-warmers-800');
                product.taxonomy.currentCategoryId !== 'towel-warmers-800' &&
                    this._cartService.removeProductsByCategory(towelWarmers800);
        }


        // add/remove TW Accessories 
        if (product.taxonomy.currentCategoryId === 'towel-warmers-200') {
            ////'Vspa Logic - hide 'DIGITAL_TIMER' );
            // let twaccesory1 = this.getProductById('ROBE_HOOK');
            // twaccesory1.isVisible = true;
            let twaccesory2 = this.getProductById('DIGITAL_TIMER');
            twaccesory2.isVisible = false;
        }
        if (product.taxonomy.currentCategoryId === 'towel-warmers-300' ||
            product.taxonomy.currentCategoryId === 'towel-warmers-500') {
            ////'Vspa Logic - Added "towel-warmers-200" category product to cart');
            // let twaccesory3 = this.getProductById('ROBE_HOOK');
            // twaccesory3.isVisible = false;
            // this._cartService.removeProduct(twaccesory3);
            let twaccesory4 = this.getProductById('DIGITAL_TIMER');
            twaccesory4.isVisible = false;
        }


        
        // BASIC BUTLER PACKAGE HIDE MX GENERATOR
        let generatorsList = ['MX4EC1', 'MX5EC1', 'MX6EC1'];
        generatorsList.forEach(generator => {
            if (product.code.toUpperCase() === generator) {
                //'Vspa Logic - Added "' + generator + '" generator to cart');

                    `1  `
                    let basicbutler = this.getProductById('BASIC_BUTLER_PACKAGE');
                    basicbutler.isVisible = false;
                    this._cartService.removeProduct(basicbutler);

                    let itempocontrols = this.getProductCategoryById('itempo-controls');
                    itempocontrols.isVisible = false;
                    this._cartService.removeProductsByCategory(itempocontrols);
                    

                    
            }
        });



        if (product.taxonomy.currentCategoryId === 'individual-controls' ||
            product.taxonomy.currentCategoryId === 'control-packages'
        ) {
            //'Vspa Logic - Added "' + product.code.toUpperCase() + '" control/package to cart');

            // Cache current control package or product
            this._appService.state.selectedControl = product;
            //'Vspa Logic - Current control:', [product]);
        }

        // Check current product if it requires generator background
        if (product.vSpa.standardGeneratorIsVisible === true) {
            this._appService.state.standardGeneratorIsVisible = true;
        }

        // Check previous products if any requires generator background
        this._cartService.cartProducts.forEach((cartProduct: Product) => {
            if (cartProduct.vSpa.standardGeneratorIsVisible === true) {

                // Display standard generator for products that do not render nice without it in background
                this._appService.state.standardGeneratorIsVisible = true;
            }
        });

        this._appService.state.standardGeneratorIsVisible === true && //'Vspa Logic - Standard generator layers is required');

        // TODO: Super products and super genrators is a separate table, isolated from the main products database

        // TODO: Anything better then this?
        setTimeout(() => {
            this._applicationRef.tick();
        }, 10);
        // this._ngZone.run( () => {} );

    }

    // TODO: Decouple. Refactor. Move to AppCatalogService

    public applyVspaProdRemoveLogic(product: Product) {
        //'Apply Vspa logic');

        if (product.code.toUpperCase() === 'XDREAM') {
            //'Vspa Logic - Removed "XDREAM" product from cart');

            // Display Spa therapies category if xDream is removed from cart
            let spaTherapies = this.getProductCategoryById('spa-therapies');
            spaTherapies.isVisible = true;
            //'Vspa Logic - Display spa therapies category');
        }

        if (product.code.toUpperCase() === 'ISTEAMX_PACKAGE' ||
            product.code.toUpperCase() === 'AIRTEMPO_PACKAGE' ||
            product.code.toUpperCase() === 'ITEMPO_PLUS_PACKAGE' ||
            product.code.toUpperCase() === 'ITEMPO_PACKAGE'
        ) {
            // // 'Vspa Logic - Added "control-packages" product to cart' );

            // Hide control accessories category if control package is selected
            let controlsAccessories = this.getProductCategoryById('controls-accessories');
            controlsAccessories.isVisible = false;
            //'Vspa Logic - Hide control accessories category');

            // Remove control accessories from cart if control package is selected
            this._cartService.removeProductsByCategory(controlsAccessories);
        }


        if (
            product.code.toUpperCase() === 'BASIC_BUTLER_PACKAGE'
            ) {
                // // 'Vspa Logic - Remove "control-packages" product from cart' );
    
                // Display control accessories if control package is added to cart
                let controlsAccessories = this.getProductCategoryById('controls-accessories-2');
                controlsAccessories.isVisible = false;
                //'Vspa Logic - Display control accessories category');

                // Remove control accessories from cart if control package is selected
                this._cartService.removeProductsByCategory(controlsAccessories);
            }


        //if (product.code.toUpperCase() === 'ISTEAMX_PACKAGE') {
           // //'Vspa Logic - Removed "iSteam" product from cart');

            // Display control accessories if control package is removed from cart
         //   let spaTherapies = this.getProductCategoryById('spa-therapies');

            // This is already too much, changing prices depending on selected products
         //   spaTherapies.products[0].price = 1300;
         //   //'Changed AudioWizard price:', spaTherapies.products[0].price);
       // }


        if (product.code.toUpperCase() === 'XDREAM_PACKAGE') {
            //'Vspa Logic - Removed "xDream" product from cart');

            // Display control accessories if control package is removed from cart
            let spaTherapies = this.getProductCategoryById('spa-therapies');
            spaTherapies.isVisible = true;
            //'Vspa Logic - Dispaly "spa-therapies" category');

            // <!> "bath-and-body-therapies" category was removed
            // Display bath and body therapies if control package is removed from cart
            // let bathAndBodyTherapies = this.getProductCategoryById('bath-and-body-therapies');
            // bathAndBodyTherapies.isVisible = true;
            // //'Vspa Logic - Dispaly "bath-and-body-therapies" category');
        }

        // if (product.code.toUpperCase() === 'XDREAM_PACKAGE' ||
        //     product.code.toUpperCase() === 'IBUTLER_PACKAGE' ||
        //     product.code.toUpperCase() === 'AIRBUTLER_PACKAGE' ||
        //     product.code.toUpperCase() === 'BUTLER_PACKAGE'
        // ) {
        //     // // 'Vspa Logic - Remove "control-packages" product from cart' );
        //
        //     // Display control accessories if control package is removed from cart
        //     let controlsAccessories = this.getProductCategoryById( 'controls-accessories' );
        //     controlsAccessories.isVisible = true;
        //     // 'Vspa Logic - Display control accessories category' );
        // }

        if (product.taxonomy.currentCategoryId === 'towel-warmers-200') { // ||
            // product.taxonomy.currentCategoryId === 'towel-warmers-300' ||
            // product.taxonomy.currentCategoryId === 'towel-warmers-500') {
            //'Vspa Logic - Removed "towel-warmers-200" category product from cart');
            // accessories exceptions for the when deselect XDREAM_PACKAGE, XBUTLER_PACKAGE, ISTEAMX_PACKAGE
        if (product.code.toUpperCase() === 'ISTEAMX_PACKAGE' || product.code.toUpperCase() === 'XDREAM_PACKAGE' || product.code.toUpperCase() === 'XBUTLER_PACKAGE' || product.code.toUpperCase() === 'AIRTEMPO_PACKAGE' || product.code.toUpperCase() === 'AIRBUTLER_PACKAGE' || product.code.toUpperCase() === 'BUTLER_PACKAGE' || product.code.toUpperCase() === 'ITEMPO_PACKAGE' || product.code.toUpperCase() === 'ITEMPO_PLUS_PACKAGE') {          
            // show CHROMASTEAMX
            let twaccesory2 = this.getProductById('CHROMASTEAMX');
            twaccesory2.isVisible = true;
            let twaccesory3 = this.getProductById('AUDIOX');
            twaccesory3.isVisible = true;
            let twaccesory4 = this.getProductById('CHROMASTEAM');
            twaccesory4.isVisible = true;
        }
            // TODO: Investigate which lifecycle hooks to use
            // Hide towel warmers accessories if Towel warmer S200 is removed from cart
            let towelWarmerAccessoriesCat = this.getProductCategoryById('towel-warmers-accessories');
            towelWarmerAccessoriesCat.isVisible = false;
            //'Vspa Logic - Hide towel warmers accessories category');
        }

        if (product.taxonomy.currentCategoryId === 'isteamx-touch-screen-controls' ||
            product.taxonomy.currentCategoryId === 'airtempo-wireless-controls' ||
            product.taxonomy.currentCategoryId === 'itempo-controls'||
            product.taxonomy.currentCategoryId === 'itempo-plus-controls'
        ) {
            //'Vspa Logic - Show linear steamhead from categories if selected control with linear option selected was removed from cart');

            // TODO: Use selectedControl from dedicated service.
            let linearSteamHead = this.getProductById('LINEAR_STEAMHEAD');
            linearSteamHead.isVisible = true;
            // this._cartService.removeProduct( linearSteamHead ); // Could be implemented
        }

        // if (product.taxonomy.currentCategoryId === 'towel-warmers-200' &&
        //     this._cartService.replacementProduct === null
        // ) {
        //     //'Vspa Logic - Removed "towel-warmers-200" category product from cart. \nNo replacement product declared. Category remains empty.');

        //     // Remove towel warmers accessories if no towel warmer series 200 is selected
        //     // TODO: _cartService.removeProductsByCategoryId() would be useful
        //     // TODO: <!> Renaming required: getProductCategoryById and getProductsByCategoryId - easy to confuse
        //     let towelWarmersAccessories = this.getProductCategoryById('towel-warmers-accessories');
        //     debugOff('Towel warmers:', towelWarmersAccessories);
        //     this._cartService.removeProductsByCategory(towelWarmersAccessories);
        //     //'Vspa Logic - Remove towel warmers accessories from cart');
        // }

        // TODO: Generate generators list dynamically, Decouple
        let generatorsList = ['MSSUPER1EC1', 'MSSUPER2EC1', 'MSSUPER3EC1', 'MX4EC1', 'MX5EC1', 'MX6EC1'];
        generatorsList.forEach(generator => {
            if (product.code.toUpperCase() === generator) {
                //'Vspa Logic - Removed "' + generator + '" generator from cart');

                // Display iTempo Wireless control if Super generator is selected
                let iTempoPack = this.getProductById('ITEMPO_PACKAGE');
                iTempoPack.isVisible = true;
                this._cartService.removeProduct(iTempoPack);
            }
        });

        // if (product.taxonomy.currentCategoryId === 'generators' &&
        //     this._cartService.replacementProduct === null
        // ) {
        //     // 'Vspa Logic - Removed "generators" category product from cart. \nNo replacement product declared. Category remains empty.' );
        //
        //     // If generator is removed, reset calculator to start
        //     this._generatorsService.resetCalculatorInputs();
        //     // 'Vspa Logic - Generators calculators form filers are cleared.' );
        // }

        if (product.taxonomy.currentCategoryId === 'individual-controls' ||
            product.taxonomy.currentCategoryId === 'control-packages'
        ) {
            //'Vspa Logic - Removed "' + product.code.toUpperCase() + '" control/package from cart');

            // Cache current control package or product
            this._appService.state.selectedControl = null;
            //'Vspa Logic - Current control:', null);
        }
        // Remove standard generator layer
        this._appService.state.standardGeneratorIsVisible = false;

        // TODO: Anything better then this?
        setTimeout(() => {
            this._applicationRef.tick();
        }, 10);
    }

}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./app/catalog/shared/services/catalog-service.ts